import React from 'react';
import PropTypes from 'prop-types';

import * as d3 from 'd3';
import map from 'lodash/map';
import max from 'lodash/max';
import prop from 'lodash/property';

import { round4, round6 } from 'utilities/math';
const RELATIVE_FREQUENCY = 'Relative Frequency';
const FREQUENCY = 'Frequency';

const canvasMargin = {
  top: 20,
  left: 100,
  right: 20,
  bottom: 20
};

const binsMargin = {
  top: 0,
  right: 0,

  left: 20,
  bottom: 20
};

class HistogramComponent extends React.PureComponent {
  constructor (props) {
    super(props);

    this.getHistogramBins = () => {
      const { values, originalValues, withBinWidth, binWidth, startPoint } = this.props;

      if (originalValues) {
        const bothValues = [...originalValues, ...values];

        let bothValuesDomain = [];
        let thresholdsRange = [];

        if (withBinWidth) {
          bothValuesDomain = d3.extent(bothValues);
          bothValuesDomain[0] = startPoint;
          thresholdsRange = d3.range(...bothValuesDomain, binWidth);
          bothValuesDomain[1] = bothValuesDomain[0] + (thresholdsRange.length * binWidth);

          // case when max value from data is equal end of bins
          if (bothValuesDomain[1] === max(bothValues)) {
            thresholdsRange.push(max(bothValues));
            bothValuesDomain[1] += binWidth;
          }

        } else {
          bothValuesDomain = d3.extent(bothValues);
          const thresholds =
            d3.thresholdFreedmanDiaconis(
              bothValues, ...bothValuesDomain
            );
          thresholdsRange = d3.range(...bothValuesDomain, (bothValuesDomain[1] - bothValuesDomain[0]) / thresholds);
        }

        const histogram = d3
          .histogram()
          .domain(bothValuesDomain)
          .thresholds(thresholdsRange);

        const bothBins = histogram(bothValues);

        return map(bothBins, ({ x0, x1 }) => {
          const bin = values.filter(value =>
            x0 <= value && value < x1
          );

          bin.x0 = x0;
          bin.x1 = x1;

          return bin;
        });
      } else {
        let valuesDomain = [];
        let thresholdsRange = [];

        if (withBinWidth) {
          valuesDomain = d3.extent(values);

          valuesDomain[0] = startPoint;
          thresholdsRange = d3.range(...valuesDomain, binWidth).map(item => round6(item));
          valuesDomain[1] = valuesDomain[0] + (thresholdsRange.length * binWidth);
          let endOfBins = round6(thresholdsRange[thresholdsRange.length - 1] + binWidth);

          // case when all values are equal
          if (thresholdsRange.length === 0) {
            [endOfBins] = valuesDomain;
          }

          // case when max value from data is equal end of bins
          if (endOfBins === max(values)) {
            thresholdsRange.push(max(values));
            valuesDomain[1] += binWidth;
          }
        } else {
          valuesDomain = d3.extent(values);
          const thresholds =
            d3.thresholdFreedmanDiaconis(
              values, ...valuesDomain
            );

          thresholdsRange = d3.range(...valuesDomain, (valuesDomain[1] - valuesDomain[0]) / thresholds);
        }

        const histogram = d3
          .histogram()
          .domain(valuesDomain)
          .thresholds(thresholdsRange);
        return histogram(values);
      }
    };

    this.getTitleTooltip = (count, total) => {
      if (this.props.isRelativeFrequency) {
        return round4(count / total);
      }
      return `${count} Observation${count > 1 ? 's' : ''} Total`;
    };

    this.createHistogram = () => {
      const binsWidthMargin = binsMargin.left + binsMargin.right;
      const canvasWidthMargin = canvasMargin.left + canvasMargin.right;
      const width = this.props.canvasWidth - canvasWidthMargin - binsWidthMargin;

      const binsHeightMargin = binsMargin.top + binsMargin.bottom;
      const canvasHeightMargin = canvasMargin.top + canvasMargin.bottom;
      const height = this.props.canvasHeight - canvasHeightMargin - binsHeightMargin;

      const { values, originalValues, withBinWidth, binWidth, startPoint, isRelativeFrequency } = this.props;

      const xDomain = d3.extent(
        originalValues
          ? [
            ...values,
            ...originalValues
          ]
          : values
      );

      const bins = this.getHistogramBins();

      if (withBinWidth) {
        xDomain[0] = startPoint;
        xDomain[1] = xDomain[0] + (bins.length * binWidth);
      }

      const x = d3
        .scaleLinear()
        .domain(xDomain)
        .rangeRound([0, width]);

      let yMaxDomain = d3.max(bins, prop('length'));
      const total = values.length;
      if (isRelativeFrequency) {
        yMaxDomain = round4(yMaxDomain / total);
      }

      const y = d3
        .scaleLinear()
        .range([height, 0])
        .domain([0, yMaxDomain]);
      const tooltip = d3.select('#tooltip');

      const svg = d3
        .select(this.svg)
        .select('#container.histogram')
        .attr('transform', `
            translate(
              ${canvasMargin.left},
              ${canvasMargin.top}
            )
          `);
      const yAxisTicks = y.ticks()
        .filter(tick => isRelativeFrequency ? tick : Number.isInteger(tick));

      svg
        .select('#y-axis')
        .transition()
        .duration(500)
        .attr('class', 'y axis')
        .style('font-size', '14px')
        .call(d3.axisLeft(y)
          .tickValues(yAxisTicks)
          .tickFormat(
            d3.format(`${isRelativeFrequency ? '~f' : 'd' }`))
        );

      svg
        .select('#x-axis-container')
        .attr('class', 'x axis')
        .attr('transform', `
            translate(
              ${binsMargin.left},
              ${height + binsMargin.bottom}
            )
          `);

      svg
        .select('#x-axis')
        .style('font-size', '14px')
        .call(d3.axisBottom(x));

      if (this.props.xAxisLabel) {
        svg
          .select('#x-axis-label')
          .text(this.props.xAxisLabel)
          .attr('dx', width);
      }

      svg.selectAll('.bar').remove();

      const binsContainer = svg
        .select('#bins-container')
        .attr('transform', `translate(${binsMargin.left}, ${isRelativeFrequency ? height : 0})`);

      const bar = binsContainer
        .selectAll('.bar')
        .data(bins)
        .enter()
        .append('g')
        .attr('class', 'bar')
        // for relative frequency: transform <= 0, that's why rounding use floor.ceil method.
        // for usual frequency: should use Math.ceil
        .attr('transform', d => `translate(${x(d.x0)}, ${isRelativeFrequency ? Math.floor((y(d.length) - height) / total) : Math.ceil(y(d.length))})`);

      bar
        .append('rect')
        .attr('x', 1)
        .attr('fill', this.props.fill)
        .attr('height', d => {
          // for relative frequency: values heights should be equal in absolute values transforms. it's always >= 0, that's why use method Math.ceil
          // for usual frequency: should use Math.ceil

          if (isRelativeFrequency) {
            return Math.max(Math.ceil((height - y(d.length)) / total), 0);
          }
          return Math.max(Math.ceil(height - y(d.length)), 0);
        })
        .attr('width', d => Math.max(x(d.x1) - x(d.x0) - 1, 0))
        .on('mousemove', (event, d) => {
          const style = `
              background-color: #5d5d5d;
              border-radius: 5px;
              font-size: 12px;
              opacity: 0.94;
              padding: 6px;
              color: white;
            `;

          tooltip.html(`
              <div style="${style}">
                <span>${this.getTitleTooltip(d.length, total)}</span>
              </div>
            `)
            .attr('class', 'absolute')
            .style('top', `${event.pageY - 35}px`)
            .style('left', `${event.pageX - 60}px`);
        })
        .on('mouseleave', () => {
          tooltip.attr('class', 'hide');
        });
    };

    this.saveSvgNode = svg => {
      this.svg = svg;
    };
  }


  componentDidMount () {
    this.createHistogram();
  }

  componentDidUpdate () {
    this.createHistogram();
  }

  render () {
    return (
      <svg
        ref={this.saveSvgNode}
        width={this.props.canvasWidth}
        height={this.props.canvasHeight}
        data-test="histogram"
      >
        <g id="container" className="histogram">
          <g id="y-axis-container">
            <g id="y-axis" />

            <text
              id="y-axis-label"

              dy="15"
              fontSize="16"
              textAnchor="end"
              transform="rotate(-90)"
            >
              {this.props.isRelativeFrequency ? RELATIVE_FREQUENCY : FREQUENCY}
            </text>
          </g>

          <g id="x-axis-container">
            <g id="x-axis" />

            <text
              id="x-axis-label"

              dy="-5"
              fontSize="16"
              textAnchor="end"
            />
          </g>

          <g id="bins-container" />
        </g>
      </svg>
    );
  }
}

HistogramComponent.propTypes = {
  values: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired
  ]).isRequired,

  originalValues: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired
  ]),

  canvasWidth: PropTypes.number.isRequired,
  canvasHeight: PropTypes.number.isRequired,
  isRelativeFrequency: PropTypes.bool.isRequired,

  fill: PropTypes.string,

  xAxisLabel: PropTypes.string,

  binWidth: PropTypes.number,
  withBinWidth: PropTypes.bool,

  startPoint: PropTypes.number
};

HistogramComponent.defaultProps = {
  fill: 'steelblue',

  xAxisLabel: null,
  originalValues: null,

  binWidth: 0,
  withBinWidth: false
};

export default HistogramComponent;
